import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAuth, useSignUp } from '@clerk/react';
import { analytics } from '@client/analytics';
import {
  clerkMessage,
  formatByCountry,
  stripDigits,
  validateForCountry,
  type SupportedCountryCode,
} from '@client/util/clerkAuthHelpers';

export { SUPPORTED_COUNTRIES, type SupportedCountryCode } from '@client/util/clerkAuthHelpers';

const emailFieldSchema = z
  .string()
  .trim()
  .min(1, 'Email is required')
  .toLowerCase()
  .email('Please enter a valid email address');

export const clerkSignupSchema = z
  .object({
    email: emailFieldSchema,
    countryCode: z.enum(['+1', '+91']),
    phoneNumber: z.string().min(1, 'Phone number is required'),
  })
  .superRefine((val, ctx) => {
    const err = validateForCountry(stripDigits(val.phoneNumber), val.countryCode);
    if (err) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phoneNumber'], message: err });
  });

export const clerkSignupSchemaEmailOnly = z.object({
  email: emailFieldSchema,
  countryCode: z.enum(['+1', '+91']),
  phoneNumber: z.string().optional(),
});

export type ClerkSignupFormValues = z.infer<typeof clerkSignupSchema>;

type Step = 'start' | 'verify_phone' | 'check_email';

export { clerkMessage } from '@client/util/clerkAuthHelpers';

const MAGIC_LINK_REDIRECT = `${typeof window === 'undefined' ? '' : window.location.origin}/signup`;

export function useClerkSignupFlow() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const redirectTo = searchParams.get('redirect_to') || '/';
  const emailHint = searchParams.get('email');

  const { signUp, errors: clerkErrors, fetchStatus } = useSignUp();
  const { isSignedIn } = useAuth();

  const isEmailOnlyMode = searchParams.get('override_connection') === 'email';

  const [step, setStep] = useState<Step>('start');
  const [phoneCode, setPhoneCode] = useState('');
  const [emailLinkVerified, setEmailLinkVerified] = useState(false);
  const [phoneCodeResentAt, setPhoneCodeResentAt] = useState<number | null>(null);
  const [emailLinkResentAt, setEmailLinkResentAt] = useState<number | null>(null);

  const RESEND_COOLDOWN_MS = 15000;

  useEffect(() => {
    if (!phoneCodeResentAt) return;
    const timer = setTimeout(() => setPhoneCodeResentAt(null), RESEND_COOLDOWN_MS);
    return () => clearTimeout(timer);
  }, [phoneCodeResentAt]);

  useEffect(() => {
    if (!emailLinkResentAt) return;
    const timer = setTimeout(() => setEmailLinkResentAt(null), RESEND_COOLDOWN_MS);
    return () => clearTimeout(timer);
  }, [emailLinkResentAt]);

  const form = useForm<ClerkSignupFormValues>({
    resolver: zodResolver(isEmailOnlyMode ? clerkSignupSchemaEmailOnly : clerkSignupSchema),
    mode: 'onChange',
    defaultValues: { email: emailHint ?? '', countryCode: '+1', phoneNumber: '' },
  });

  useEffect(() => {
    if (!isSignedIn) return;
    if (redirectTo.startsWith('http')) {
      window.location.href = redirectTo;
    } else {
      navigate(redirectTo, { replace: true });
    }
  }, [isSignedIn, redirectTo, navigate]);

  useEffect(() => {
    if (step !== 'check_email') return;
    let cancelled = false;
    void signUp.verifications.waitForEmailLinkVerification().then(({ error }) => {
      if (!cancelled && !error) setEmailLinkVerified(true);
    });
    return () => {
      cancelled = true;
    };
    // Intentionally depend only on `step`. `signUp` is a new object reference on
    // every signal update — including it would re-fire the long-poll on every
    // render and trigger Clerk's 429 rate limit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const startMutation = useMutation({
    mutationFn: async (values: ClerkSignupFormValues) => {
      if (isEmailOnlyMode) {
        analytics.formSubmit('signup_form', { email: values.email });

        const { error: createError } = await signUp.create({
          emailAddress: values.email,
          legalAccepted: true,
        });
        if (createError) throw createError;

        const { error: linkError } = await signUp.verifications.sendEmailLink({
          verificationUrl: MAGIC_LINK_REDIRECT,
        });
        if (linkError) throw linkError;
        return;
      }

      const e164Phone = `${values.countryCode}${stripDigits(values.phoneNumber)}`;
      analytics.formSubmit('signup_form', { email: values.email, phoneNumber: e164Phone });

      const { error: createError } = await signUp.create({
        emailAddress: values.email,
        phoneNumber: e164Phone,
        legalAccepted: true,
      });
      if (createError) throw createError;

      const { error: smsError } = await signUp.verifications.sendPhoneCode();
      if (smsError) throw smsError;
    },
    onSuccess: () => setStep(isEmailOnlyMode ? 'check_email' : 'verify_phone'),
  });

  const verifyPhoneMutation = useMutation({
    mutationFn: async (code: string) => {
      const { error: verifyError } = await signUp.verifications.verifyPhoneCode({ code });
      if (verifyError) throw verifyError;
      const { error: linkError } = await signUp.verifications.sendEmailLink({
        verificationUrl: MAGIC_LINK_REDIRECT,
      });
      if (linkError) throw linkError;
    },
    onSuccess: () => setStep('check_email'),
  });

  const resendPhoneMutation = useMutation({
    mutationFn: async () => {
      const { error } = await signUp.verifications.sendPhoneCode();
      if (error) throw error;
    },
    onSuccess: () => {
      setPhoneCode('');
      verifyPhoneMutation.reset();
      setPhoneCodeResentAt(Date.now());
    },
  });

  const resendLinkMutation = useMutation({
    mutationFn: async () => {
      const { error } = await signUp.verifications.sendEmailLink({
        verificationUrl: MAGIC_LINK_REDIRECT,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setEmailLinkResentAt(Date.now());
    },
  });

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = stripDigits(e.target.value);
    form.setValue('phoneNumber', formatByCountry(digits, form.getValues('countryCode')), {
      shouldValidate: true,
    });
  };

  const handleCountryCodeChange = (code: SupportedCountryCode) => {
    form.setValue('countryCode', code, { shouldValidate: true });
    const digits = stripDigits(form.getValues('phoneNumber'));
    form.setValue('phoneNumber', formatByCountry(digits, code), { shouldValidate: true });
  };

  const goBack = async () => {
    setPhoneCode('');
    setEmailLinkVerified(false);
    await signUp.reset();
    setStep('start');
  };

  const isSigningIn =
    isSignedIn === true ||
    searchParams.has('__clerk_status') ||
    searchParams.has('__clerk_created_session');

  const mutationError =
    clerkMessage(verifyPhoneMutation.error as { longMessage?: string; message?: string } | null) ??
    clerkMessage(startMutation.error as { longMessage?: string; message?: string } | null);

  return {
    form,
    step,
    isSigningIn,
    emailLinkVerified,
    isEmailOnlyMode,

    onStartSubmit: form.handleSubmit((v) => startMutation.mutate(v)),
    onVerifyPhoneSubmit: (e: React.FormEvent) => {
      e.preventDefault();
      if (phoneCode.length === 6) verifyPhoneMutation.mutate(phoneCode);
    },

    handlePhoneNumberChange,
    handleCountryCodeChange,
    goBack,

    phoneCode,
    setPhoneCode,

    isStartSubmitting: startMutation.isPending || fetchStatus === 'fetching',
    isVerifying: verifyPhoneMutation.isPending,

    emailAddress: signUp.emailAddress,
    phoneNumber: signUp.phoneNumber,

    mutationError,
    clerkErrors,

    resendLink: resendLinkMutation.mutate,
    resendPhone: resendPhoneMutation.mutate,

    isResendingPhone: resendPhoneMutation.isPending,
    phoneCodeResent: phoneCodeResentAt !== null,
    isResendingLink: resendLinkMutation.isPending,
    emailLinkResent: emailLinkResentAt !== null,
  };
}
